/**
 * Agent registry service.
 *
 * FABRIC-STUB: harness /api/v1/agents does not yet exist. This facade maps the
 * Orchestrate Agents registry onto the data-service AgentFlow endpoints until
 * the harness promotion is complete.
 *
 * Data model: an "agent" in the registry is one agent embedded inside an
 * AgentFlow document (`flow.agents[]`). Because the data-service addresses
 * agents by their parent flow (`GET /agentflow/{flowId}` returns the flow + all
 * agents; there is no get-single-agent endpoint), every registry row carries a
 * composite route id `flowId.agentId`. Flows with no embedded agents fall back
 * to a flow-as-agent row keyed by `flowId` alone.
 *
 *   list   → GET    /agentflows?projectID=X
 *   read   → GET    /agentflow/{flowId}                      (find agent in .agents)
 *   create → POST   /agentflow/  then POST /agentflow/{id}/skilled
 *   update → POST   /agentflow/{flowId}/update/skilled/{agentId}  (+ PUT flow name)
 *   delete → DELETE /agentflow/{flowId}/agent/{agentId}     (or delete the flow)
 *   clone  → POST   /agentflow/copy
 */

import { dataServiceClient } from '../dataservice-client';
import { apiClient } from '../client';
import { executionsService } from './executions';
import type { AgentRiskLevel, AgentTrigger } from '../types/process-flows';
import { BLAND_DEFAULT_VOICE, normalizeBlandVoice } from '@/lib/voice/voices';
import { AUTO_MODEL_ID, AUTO_MODEL_LABEL, isAutoModel } from '@/lib/connect/auto-model';
import { riskLevelFromStored, riskLevelToStored } from '@/lib/connect/agent-risk';
import { needsReasoningFromStored, needsReasoningToStored } from '@/lib/connect/agent-reasoning';

export type AgentType = 'Conversational' | 'Task' | 'Voice' | 'Proxy' | 'Interop';
export type AgentStatus = 'Active' | 'Paused' | 'Draft';

/** Safe Bland Voice Agent settings. Credentials remain in the selected
 * project connector and are never stored on the agent or sent to the browser. */
export interface VoiceConfig {
  transport: 'webrtc' | 'phone' | 'both';
  direction: 'inbound' | 'outbound' | 'both';
  language: string;
  provider: 'bland';
  connectorId: string;
  blandAgentId?: string;
  voice: string;
  greeting: string;
  allowInterruptions: boolean;
  silenceTimeoutMs: number;
  maxCallDurationSeconds: number;
  recordCalls: boolean;
  saveTranscripts: boolean;
  phoneNumberId?: string;
  transferPhoneNumber?: string;
  fabricToolAccess: 'none' | 'read' | 'full';
}

export const DEFAULT_VOICE_CONFIG: VoiceConfig = {
  transport: 'webrtc',
  direction: 'both',
  language: 'en-US',
  provider: 'bland',
  connectorId: '',
  voice: BLAND_DEFAULT_VOICE,
  greeting: "Hello, I'm an AI voice assistant. How can I help you today?",
  allowInterruptions: true,
  silenceTimeoutMs: 400,
  maxCallDurationSeconds: 1800,
  recordCalls: false,
  saveTranscripts: true,
  fabricToolAccess: 'full',
};

export interface AgentRow {
  id: string;        // composite route id: `${flowId}` or `${flowId}.${agentId}`
  flowId: string;
  agentId?: string;
  flowName: string;  // display name of the parent agentflow
  name: string;
  desc: string;
  type: AgentType;
  status: AgentStatus;
  owner: string;
  model: string;
  /** Raw registry model id (e.g. "mdl_bx_..."), for resolving the display
   * name against the live AI Models registry — `model` above is a best-effort
   * static label that can't cover org-registered registry models. */
  modelId?: string;
  lastRun: string;
  runs: number;
  /** True when this agent is one of several in a multi-agent process flow. */
  inFlow?: boolean;
  /** Version-on-promote (Module 06): a simulated version was promoted live. */
  promoted?: boolean;
}

/**
 * The three values data-service's ContextAccessConfig accepts.
 *
 * `full`       brief in the prompt + the context_graph query skills
 * `brief_only` the Project Context Brief only, no query skills
 * `off`        the Context Graph is unavailable to this agent
 *
 * Both builders expose this as a two-ended SWITCH and only ever write the ends
 * (`full` / `off`); `brief_only` is reachable through the api-gateway manifest
 * tools. See CONTEXT_ACCESS_ON / contextAccessFromMode below.
 */
export type ContextAccessMode = 'off' | 'brief_only' | 'full';

/**
 * Is the Context-from-Graph switch ON for this stored mode?
 *
 * The one place the default lives on this surface, mirroring the backend's
 * single decision point (effective_context_access_mode) and the flow builder's
 * identical expression: a missing or empty config means ENABLED. Agents saved
 * before the field existed, and every agent created without it, must read as ON
 * — the backend already grants them full access, so rendering the switch off
 * would tell the user the opposite of what their agent can actually do.
 */
export const contextAccessIsOn = (mode?: ContextAccessMode | string | null): boolean =>
  (mode ?? 'full') !== 'off';

/** The sub-document a switch position writes. Matches AgentFlowOptions exactly. */
export const contextAccessFromSwitch = (on: boolean): { mode: ContextAccessMode; focusLabels: string[] } => ({
  mode: on ? 'full' : 'off',
  focusLabels: [],
});

export interface AgentConfig {
  systemPrompt: string;
  description?: string;
  modelId: string;
  /**
   * PRD R2: the agent's configured risk level, stored as `llm.risk_level`.
   * null = not set. Only sent on update together with `modelId` (the llm
   * object is written as a whole); undefined leaves the stored value alone.
   */
  riskLevel?: AgentRiskLevel | null;
  /**
   * Routing v2: the agent needs a reasoning model, stored as
   * `llm.needs_reasoning` (boolean, default false). Like riskLevel it is only
   * sent on update together with `modelId`; undefined leaves it alone.
   */
  needsReasoning?: boolean;
  knowledgeBaseIds: string[];
  // FAB-44: project knowledge-base attach — a toggle + context tags (backend
  // fields has_knowledge_base + context). Replaces the read-only knowledgeBaseIds view.
  hasKnowledgeBase?: boolean;
  knowledgeContext?: string[];
  // FAB-60: which Tables this agent may read/write, and in what mode. The
  // tables connector is hidden from the normal skill picker — access is
  // injected at execution time from this config, not a manual skill.
  tablesAccess?: {
    enabled: boolean;
    tableIds: string[];
    mode: 'read' | 'read_write';
  };
  // DESIGN-agent-context-access: whether this agent may use the ORGANIZATION's
  // Context Graph — distinct from `knowledgeContext` (project documents) and
  // from a flow's "Context from Agents" (predecessor outputs). The
  // context_graph connector is hidden from the skill picker, so access is
  // injected at run start from this config, exactly like tablesAccess.
  //
  // `undefined` means the agent doc carries no context_access at all, which the
  // backend reads as FULL access (effective_context_access_mode, agentflow.py).
  // Absent is enabled — losing graph access is always an explicit user action.
  contextAccess?: {
    mode: ContextAccessMode;
    focusLabels: string[];
  };
  // The shape the agent is expected to answer in, as a JSON Schema object.
  // Matters most for a Task Agent: it has no conversation, so "trigger in,
  // structured output out" is its whole contract. Metadata only — nothing
  // validates a run's output against it today.
  outputSchema?: Record<string, unknown>;
  voiceConfig?: VoiceConfig;
  toolIds: string[];
  skillIds: string[];
  guardrailIds: string[];
  // FAB-182: fire-and-forget hand-offs to another process flow when this
  // agent finishes. Previously configurable only inside a multi-agent flow's
  // builder — a standalone agent had no trigger UI at all.
  triggers?: AgentTrigger[];
  memoryConfig: {
    longTermMemory: boolean;
    piiRedaction: boolean;
    topicBoundaries: boolean;
    jailbreakDefense: boolean;
  };
}

export interface Agent extends AgentRow {
  config: AgentConfig;
  version: number;
  riskClass?: string;
  // The data-service organization the parent flow belongs to. This is the
  // org executions/model-config resolve against — and it differs from the
  // identity org in the frontend scope store, so model-config lookups must use
  // THIS value, not useScopeStore().org.
  organizationId?: string;
}

export interface CreateAgentPayload {
  name: string;
  /** "What should this agent do?" — seeds the system prompt. */
  desc?: string;
  /** Short card description (separate from the goal/prompt). */
  description?: string;
  type: AgentType;
  model?: string;
  /** Stored as `llm.risk_level`; null/absent = not set. */
  riskLevel?: AgentRiskLevel | null;
  /** Stored as `llm.needs_reasoning`; absent = false. */
  needsReasoning?: boolean;
  projectId: string;
  /** Skill ObjectId strings to attach to the new agent at creation time. */
  skillIds?: string[];
  /** Required by the Voice Agent runtime, ignored for all other agent types. */
  voiceConfig?: VoiceConfig;
}

// SPEC-interop-agent-v2.md §3: which kind of thing a registration is.
// 'tool_catalog' (default, v1 behavior — Capabilities in the response) vs
// 'single_peer_agent' (new — Card in the response instead, one action row,
// no "pick a tool" step, since there isn't one).
export type InteropShape = 'tool_catalog' | 'single_peer_agent';

// A single_peer_agent registration's discovered identity — captured once at
// Discover time, mirrors harness's AgentCard (model.go / external_agent.go).
export interface InteropAgentCard {
  name: string;
  description: string;
  skills?: string[];
  protocolVersion?: string;
  supportsCancel: boolean;
}

export interface InteropDiscoverPayload {
  platform: string;
  protocol: string;
  endpoint: string;
  auth: 'oauth' | 'key' | 'mtls';
  /** API key / bearer token for `auth: 'key'` endpoints. OAuth/mTLS credential
   * exchange is v2 (SPEC-interop-agent.md §3) — omit for those auth types. */
  secret?: string;
  /** Defaults to 'tool_catalog' server-side when omitted. */
  shape?: InteropShape;
}

export interface Capability {
  name: string;
  kind: 'action' | 'read';
  hitlSuggested: boolean;
  /** Real MCP inputSchema (JSON Schema) for this tool, captured on Discover —
   * lets a human (or the agent, at run time) see exactly what this tool
   * needs before using it, instead of guessing. */
  schema?: { properties?: Record<string, { type?: string; description?: string }>; required?: string[] };
  /** Real MCP outputSchema, when the external server declares one (optional
   * per the MCP spec — many tools omit it). Display-only, for the tool's
   * documentation panel. */
  outputSchema?: { properties?: Record<string, { type?: string; description?: string }>; required?: string[] };
  /** The tool's own short description from tools/list — shown next to its
   * name in the skill/tool picker. */
  description?: string;
}

// Mirrors harness's Outcome (external_agent.go) exactly — what a
// single_peer_agent Invoke/Resume call returns.
export interface InteropOutcome {
  kind: 'completed' | 'failed' | 'pending';
  handle?: string;
  result?: Record<string, any>;
  ask?: { prompt: string; schema?: Record<string, any> };
  failErr?: string;
}

// harness's ElicitationAsk (mcp_elicitation.go) — an MCP server's mid-call
// elicitation/create question, distinct from InteropOutcome.ask (A2A's
// input-required/auth-required, returned only once the call itself has
// already come back as kind:'pending').
export interface InteropElicitationAsk {
  prompt: string;
  schema?: Record<string, any>;
}

export interface RegisterInteropPayload {
  name: string;
  description?: string;
  platform: string;
  protocol: string;
  endpoint: string;
  auth: 'oauth' | 'key' | 'mtls';
  secret?: string;
  registerAsSkill: boolean;
  projectId: string;
  shape?: InteropShape;
}

// An OUTBOUND connection Fabric registered to an external agent (harness's
// interop_registrations collection — never an agentflow, so it never appears
// in listAgents()/getAgent()).
export interface InteropRegistration {
  id: string;
  projectId: string;
  name: string;
  description: string;
  platform: string;
  protocol: string;
  endpoint: string;
  auth: 'oauth' | 'key' | 'mtls';
  skillId?: string;
  status: 'Draft' | 'Active';
  capabilities?: Capability[];
  shape?: InteropShape;
  card?: InteropAgentCard;
  createdAt: string;
  updatedAt: string;
}

// One Fabric agent that has an interop registration's Skill attached — the
// pre-delete check's result shape (mirrors harness's AgentUsingSkill).
export interface InteropAttachedAgent {
  agentflowId: string;
  agentflowName: string;
  agentId: string;
  agentTitle: string;
}

// An INBOUND API key — lets an external MCP caller reach this project's
// published (interop_enabled) agents. Raw token only ever appears once, in
// the mint response.
export interface InboundInteropKey {
  id: string;
  projectId: string;
  flowId: string;
  name: string;
  keyPrefix: string;
  createdAt: string;
  revoked: boolean;
}

// ── Reference (composite id) helpers ─────────────────────────────────────────

const REF_SEP = '.';

/** Build a registry route id from a flow id (+ optional embedded agent id). */
export function makeAgentRef(flowId: string, agentId?: string): string {
  return agentId ? `${flowId}${REF_SEP}${agentId}` : flowId;
}

/** Split a registry route id back into its flow id and embedded agent id. */
export function parseAgentRef(routeId: string): { flowId: string; agentId?: string } {
  const idx = routeId.indexOf(REF_SEP);
  if (idx === -1) return { flowId: routeId };
  return { flowId: routeId.slice(0, idx), agentId: routeId.slice(idx + 1) };
}

/**
 * Generate a MongoDB-compatible 24-hex ObjectId. The data-service's
 * `POST /agentflow/{id}/skilled` (SkilledAgentInput → BaseAgent) requires the
 * client to supply the agent's `_id`, and the frontend has no bson library.
 * 8 hex of unix-seconds + 16 hex of randomness = a valid ObjectId string.
 */
function genObjectId(): string {
  const ts = Math.floor(Date.now() / 1000).toString(16).padStart(8, '0');
  let rand = '';
  for (let i = 0; i < 16; i++) rand += Math.floor(Math.random() * 16).toString(16);
  return (ts + rand).slice(0, 24);
}

// ── Model id ↔ display-label mapping ─────────────────────────────────────────
// The builder renders model *labels*; MongoDB stores model *ids*. Keep both
// directions so reads light up the right button and writes persist a real id.

const MODEL_TO_LABEL: Record<string, string> = {
  // Current IDs
  'claude-sonnet-4-6': 'Claude Sonnet 4.6',
  'claude-opus-4-8':   'Claude Opus 4.8',
  'claude-haiku-4-5':  'Claude Haiku 4.5',
  // Full AWS Bedrock inference-profile / model IDs persisted by the data-service.
  // These are what `agent.llm.model` actually holds in MongoDB, so a registry
  // row that doesn't match here falls through to the raw id (the P1-02 bug).
  'anthropic.claude-haiku-4-5-20251001-v1':    'Claude Haiku 4.5',
  'anthropic.claude-haiku-4-5-20251001-v1:0':  'Claude Haiku 4.5',
  'anthropic.claude-sonnet-4-6-20250930-v1':   'Claude Sonnet 4.6',
  'anthropic.claude-sonnet-4-6-20250930-v1:0': 'Claude Sonnet 4.6',
  'anthropic.claude-opus-4-8-20251101-v1':     'Claude Opus 4.8',
  'anthropic.claude-opus-4-8-20251101-v1:0':   'Claude Opus 4.8',
  // Legacy IDs kept for backward compat
  'claude-sonnet-4-5': 'Claude Sonnet 4.5',
  'claude-opus-4':     'Claude Opus 4',
  'claude-opus-4-1':   'Claude Opus 4',
  'gpt-4o':            'GPT-4o',
  'azure/gpt-4o':      'GPT-4o (Azure)',
  'llama-3.1-70b':     'Llama 3.1 70B',
  'gemini-2.0-pro':    'Gemini 2.0 Pro',
};

// Bedrock IDs come dressed up with a region prefix (`us.`, `eu.`), a provider
// prefix (`anthropic.`), a date stamp, and a version suffix (`-v1:0`). Reduce
// any Anthropic variant to a Claude family label without enumerating every date.
function bedrockFallbackLabel(id: string): string | null {
  const m = id.toLowerCase().match(/claude[.-](haiku|sonnet|opus)[.-](\d+)[.-](\d+)/);
  if (!m) return null;
  const family = m[1][0].toUpperCase() + m[1].slice(1);
  return `Claude ${family} ${m[2]}.${m[3]}`;
}

const LABEL_TO_MODEL: Record<string, string> = {
  'Claude Sonnet 4.6': 'claude-sonnet-4-6',
  'Claude Opus 4.8':   'claude-opus-4-8',
  'Claude Haiku 4.5':  'claude-haiku-4-5',
  'Claude Sonnet 4.5': 'claude-sonnet-4-5',
  'Claude Opus 4':     'claude-opus-4',
  'GPT-4o':            'gpt-4o',
  'GPT-4o (Azure)':    'azure/gpt-4o',
  'Llama 3.1 70B':     'llama-3.1-70b',
  'Gemini 2.0 Pro':    'gemini-2.0-pro',
};

export function toModelLabel(id?: string): string {
  // No stored model → say so honestly (Rule #1: don't fake a specific model).
  // A missing model means the agent wasn't fully configured/embedded.
  if (!id) return 'No model set';
  // The Auto sentinel: data-service picks the model per step at run time.
  if (isAutoModel(id)) return AUTO_MODEL_LABEL;
  if (MODEL_TO_LABEL[id]) return MODEL_TO_LABEL[id];
  // Strip a Bedrock region prefix (us./eu./apac.) and retry the exact map.
  const stripped = id.replace(/^(us|eu|apac|global)\./i, '');
  if (MODEL_TO_LABEL[stripped]) return MODEL_TO_LABEL[stripped];
  return bedrockFallbackLabel(id) ?? id;
}

export function toModelId(label?: string): string {
  if (!label) return 'claude-sonnet-4-5';
  if (label === AUTO_MODEL_LABEL) return AUTO_MODEL_ID;
  return LABEL_TO_MODEL[label] ?? label;
}

// ── Field normalisers ────────────────────────────────────────────────────────

function normalizeType(raw: any): AgentType {
  const t = String(raw ?? '');
  if (['Conversational', 'Task', 'Voice', 'Proxy', 'Interop'].includes(t)) {
    return t as AgentType;
  }
  const v = t.toLowerCase();
  if (v.includes('voice')) return 'Voice';
  if (v.includes('proxy')) return 'Proxy';
  if (v.includes('interop')) return 'Interop';
  if (v.includes('task') || v === 'decider_agent' || v === 'decider') return 'Task';
  return 'Conversational';
}

// Resolve a status pill from whatever the AgentFlow / embedded agent exposes:
// an explicit string status, or a boolean `is_active` / `isActive` / `active`
// flag (a deployed-but-no-status agent should read Active, not Draft).
function normalizeStatus(raw: any, flags?: Record<string, any>): AgentStatus {
  const v = String(raw ?? '').toLowerCase();
  if (['active', 'deployed', 'live', 'enabled', 'running'].includes(v)) return 'Active';
  if (['paused', 'disabled', 'inactive', 'suspended'].includes(v)) return 'Paused';
  if (['draft', 'new'].includes(v)) return 'Draft';

  if (flags) {
    const flag = flags.is_active ?? flags.isActive ?? flags.active ?? flags.enabled;
    if (flag === true) return 'Active';
    if (flag === false) return 'Paused';
  }
  return 'Draft';
}

function fmtTs(ts: any): string {
  if (ts == null || ts === '') return '—';
  // Numeric epoch (seconds or ms), including numeric strings.
  const n = typeof ts === 'number' ? ts : Number(ts);
  const isNumeric = typeof ts === 'number' || /^\d+$/.test(String(ts).trim());
  const dt = isNumeric ? new Date((n < 1e12 ? n * 1000 : n)) : new Date(ts);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString();
}

// Map an embedded agent (within a flow) to a registry row.
function rowFromAgent(flow: any, agent: any): AgentRow {
  return {
    id: makeAgentRef(flow._id, agent._id),
    flowId: flow._id,
    agentId: agent._id,
    flowName: flow.name ?? flow.title ?? '',
    name: agent.title ?? agent.name ?? 'Untitled Agent',
    desc: agent.description ?? agent.role_description ?? '',
    // UI type lives in the dedicated `ui_type` field; fall back to `role`
    // (older wizard agents stored the type there) then the structural agent_type.
    type: normalizeType(agent.ui_type ?? flow.ui_type ?? agent.type ?? agent.role ?? agent.agent_type),
    status: normalizeStatus(agent.status ?? flow.status, { ...flow, ...agent }),
    owner: agent.owner ?? flow.created_by ?? '—',
    model: toModelLabel(agent.llm?.model ?? agent.model),
    modelId: agent.llm?.model ?? agent.model,
    lastRun: fmtTs(flow.updated_at ?? flow.updatedAt ?? flow.last_run ?? flow.created_at),
    runs: agent.runs ?? 0,
    promoted: Boolean(agent.is_promoted),
  };
}

// Map a flow with no embedded agents to a flow-as-agent row.
function rowFromFlow(flow: any): AgentRow {
  return {
    id: makeAgentRef(flow._id),
    flowId: flow._id,
    flowName: flow.name ?? flow.title ?? '',
    name: flow.name ?? flow.title ?? 'Untitled Agent',
    desc: flow.description ?? flow.desc ?? '',
    type: normalizeType(flow.ui_type ?? flow.type),
    status: normalizeStatus(flow.status, flow),
    owner: flow.created_by ?? '—',
    model: toModelLabel(flow.llm?.model ?? flow.model),
    modelId: flow.llm?.model ?? flow.model,
    lastRun: fmtTs(flow.updated_at ?? flow.updatedAt ?? flow.last_run ?? flow.created_at),
    runs: flow.runs ?? 0,
    promoted: Boolean(flow.is_promoted ?? (Array.isArray(flow.agents) && flow.agents.some((a: any) => a?.is_promoted))),
  };
}

export const agentsService = {
  // ── List agents ─────────────────────────────────────────────────────────────
  // FABRIC-STUB: flattens agents out of all AgentFlows for the given project.
  async listAgents(projectId: string, params?: { search?: string; type?: AgentType }): Promise<AgentRow[]> {
    // kind=agent: the Agents registry shows only standalone single-agent
    // containers. Multi-agent process flows live on the Multi-Agent Flows page
    // (kind=flow) and no longer leak into this list.
    const res = await dataServiceClient.get<{ agentflows: any[]; total_count: number }>(
      `/agentflows?projectID=${encodeURIComponent(projectId)}&kind=agent&skip=1&limit=100${params?.search ? `&search_key=${encodeURIComponent(params.search)}` : ''}`,
    );
    const flows: any[] = res.agentflows ?? [];
    const rows: AgentRow[] = [];
    for (const flow of flows) {
      const agents: any[] = flow.agents ?? [];
      // An agent-kind container holds exactly one agent. Emit one row per agent;
      // a container with no agent yet falls back to a flow-as-agent placeholder.
      // Agent rows never belong to a multi-agent flow, so the "Flow" badge and
      // parent-flow subtitle are suppressed (flowName cleared).
      if (agents.length >= 1) {
        for (const a of agents) {
          const row = rowFromAgent(flow, a);
          row.inFlow = false;
          row.flowName = '';
          if (!params?.type || row.type === params.type) rows.push(row);
        }
      } else {
        const row = rowFromFlow(flow);
        row.flowName = '';
        if (!params?.type || row.type === params.type) rows.push(row);
      }
    }

    // The AgentFlow document itself carries no run counter (agent.runs /
    // flow.runs are never populated by the data-service), so `runs` above is
    // always 0. The real count lives in the execution_flows collection —
    // fetch each row's flow's total execution count (one lightweight
    // limit=1 history call per UNIQUE flow, in parallel) and patch it in.
    // Interop (outbound) rows never reach here (merged in by the caller,
    // agents/page.tsx, after this resolves) so every row here is a real
    // agentflow with a flowId worth counting.
    const uniqueFlowIds = Array.from(new Set(rows.map((r) => r.flowId).filter(Boolean)));
    if (uniqueFlowIds.length > 0) {
      const counts = await Promise.all(
        uniqueFlowIds.map((flowId) =>
          executionsService
            .getExecutionHistory({ projectID: projectId, agentflow_id: flowId, limit: 1, page: 0 })
            .then((h) => h.totalCount ?? 0)
            .catch(() => 0),
        ),
      );
      const runsByFlowId = new Map(uniqueFlowIds.map((id, i) => [id, counts[i]]));
      for (const row of rows) row.runs = runsByFlowId.get(row.flowId) ?? 0;
    }

    return rows;
  },

  // ── Resolve a project's data-service org ─────────────────────────────────────
  // Model-config / execution lookups key off the data-service organization_id,
  // which differs from the frontend identity org. The list projection omits it,
  // so resolve via one flow's full document. Returns '' when the project has no
  // flows yet (caller falls back to the unfiltered model list).
  async resolveProjectOrgId(projectId: string): Promise<string> {
    try {
      const list = await dataServiceClient.get<{ agentflows: any[] }>(
        `/agentflows?projectID=${encodeURIComponent(projectId)}&skip=1&limit=1`,
      );
      const flowId = list.agentflows?.[0]?._id;
      if (!flowId) return '';
      const flow = await dataServiceClient.get<any>(
        `/agentflow/${encodeURIComponent(flowId)}?projectID=${encodeURIComponent(projectId)}`,
      );
      return (flow.agentflow ?? flow).organization_id ?? '';
    } catch {
      return '';
    }
  },

  // ── Get single agent ─────────────────────────────────────────────────────────
  // Resolves a composite `flowId.agentId` route id → fetches the parent flow →
  // locates the embedded agent. Falls back to the first agent / the flow itself.
  async getAgent(routeId: string, projectId: string): Promise<Agent> {
    const { flowId, agentId } = parseAgentRef(routeId);
    const res = await dataServiceClient.get<any>(
      `/agentflow/${encodeURIComponent(flowId)}?projectID=${encodeURIComponent(projectId)}`,
    );
    const flow = res.agentflow ?? res;
    const agents: any[] = flow.agents ?? [];
    const agent = agentId
      ? agents.find((a) => String(a._id) === agentId) ?? agents[0]
      : agents[0];

    const base = agent ? rowFromAgent(flow, agent) : rowFromFlow(flow);
    // Keep the route id stable even if we fell back to the first agent.
    base.id = routeId;

    const systemPrompt =
      agent?.prompt?.template ?? agent?.system_prompt ?? flow.role ?? flow.description ?? '';

    return {
      ...base,
      config: {
        systemPrompt,
        description: agent?.description ?? agent?.role_description ?? flow.description ?? '',
        // Return the raw model ID from the backend (not a display label) so the
        // builder can match it against org models fetched from modelsService.
        modelId: agent?.llm?.model ?? agent?.model ?? 'claude-sonnet-4-6',
        riskLevel: riskLevelFromStored(agent?.llm?.risk_level) || null,
        needsReasoning: needsReasoningFromStored(agent?.llm?.needs_reasoning),
        knowledgeBaseIds: agent?.knowledge_base_ids ?? [],
        // FAB-44: project KB attach (backend uses has_knowledge_base + context tags).
        hasKnowledgeBase: agent?.has_knowledge_base ?? false,
        knowledgeContext: agent?.context ?? [],
        outputSchema: agent?.output_schema ?? undefined,
        voiceConfig: agent?.voice_config ? {
          transport: agent.voice_config.transport ?? DEFAULT_VOICE_CONFIG.transport,
          direction: agent.voice_config.direction ?? DEFAULT_VOICE_CONFIG.direction,
          language: agent.voice_config.language ?? DEFAULT_VOICE_CONFIG.language,
          provider: 'bland',
          connectorId: agent.voice_config.connector_id ?? '',
          blandAgentId: agent.voice_config.bland_agent_id ?? undefined,
          voice: normalizeBlandVoice(
            agent.voice_config.voice ?? agent.voice_config.tts_voice,
          ),
          greeting: agent.voice_config.greeting ?? DEFAULT_VOICE_CONFIG.greeting,
          allowInterruptions: agent.voice_config.allow_interruptions ?? true,
          silenceTimeoutMs: Math.min(10000, Math.max(50, agent.voice_config.silence_timeout_ms ?? DEFAULT_VOICE_CONFIG.silenceTimeoutMs)),
          maxCallDurationSeconds: agent.voice_config.max_call_duration_seconds ?? DEFAULT_VOICE_CONFIG.maxCallDurationSeconds,
          recordCalls: agent.voice_config.record_calls ?? false,
          saveTranscripts: agent.voice_config.save_transcripts ?? true,
          phoneNumberId: agent.voice_config.phone_number_id ?? undefined,
          transferPhoneNumber: agent.voice_config.transfer_phone_number ?? undefined,
          fabricToolAccess: agent.voice_config.fabric_tool_access ?? DEFAULT_VOICE_CONFIG.fabricToolAccess,
        } : undefined,
        tablesAccess: {
          enabled: agent?.tables_access?.enabled ?? false,
          tableIds: agent?.tables_access?.table_ids ?? [],
          mode: agent?.tables_access?.mode ?? 'read',
        },
        // Left UNDEFINED when the agent doc carries no context_access, rather
        // than defaulted to a mode here. The absence is meaningful — it is what
        // the backend reads as full access — and inventing `{mode:'full'}` at
        // this boundary would make an unconfigured agent indistinguishable from
        // one a user explicitly switched on. The switch resolves the default
        // through contextAccessIsOn instead.
        contextAccess: agent?.context_access
          ? {
              mode: (agent.context_access.mode ?? 'full') as ContextAccessMode,
              focusLabels: agent.context_access.focus_labels ?? [],
            }
          : undefined,
        toolIds: agent?.tool_ids ?? [],
        skillIds: (agent?.skills ?? []).map((s: any) =>
          typeof s === 'string' ? s : s?._id ?? '',
        ).filter(Boolean),
        guardrailIds: agent?.guardrail_ids ?? [],
        triggers: (agent?.triggers ?? []).map((t: any) => ({
          _id: t?._id ? String(t._id) : undefined,
          name: t?.name ?? '',
          goal: t?.goal ?? '',
          agentflow_id: t?.agentflow_id ?? '',
          start_from_agent_id: t?.start_from_agent_id ?? '',
        })),
        memoryConfig: agent?.memory_config ?? {
          longTermMemory: true,
          piiRedaction: true,
          topicBoundaries: true,
          jailbreakDefense: true,
        },
      },
      version: flow.version ?? 1,
      organizationId: flow.organization_id ?? flow.organizationId ?? '',
    };
  },

  // ── Create agent ─────────────────────────────────────────────────────────────
  // FABRIC-STUB: creates an AgentFlow with a single skilled agent and returns a
  // row whose route id points at that embedded agent.
  async createAgent(payload: CreateAgentPayload): Promise<AgentRow> {
    const flow = await dataServiceClient.post<any>(
      `/agentflow/?projectID=${encodeURIComponent(payload.projectId)}`,
      {
        name: payload.name,
        description: payload.description ?? '',
        projectID: payload.projectId,
        // `role` is required by CreateAgentflowInput (data-service 422s without
        // it) AND is the field normalizeType falls back to, so it must carry the
        // UI type until `ui_type` is actually wired on the backend models
        // (currently undeclared → Pydantic drops it, so it never persists).
        role: payload.type ?? 'Conversational',
        // Registry discriminator: this container is a single standalone agent,
        // so it must surface only on the Agents page (never as a process flow).
        kind: 'agent',
        // UI agent type — kept for when the backend declares `ui_type`; today it
        // is dropped, hence `role` above is the live type carrier.
        ui_type: payload.type,
      },
    );
    const flowId = flow._id ?? flow.id ?? '';

    // The data-service requires the client to supply the new agent's _id.
    let agentId: string | undefined = genObjectId();
    try {
      await dataServiceClient.post<any>(
        `/agentflow/${encodeURIComponent(flowId)}/skilled`,
        {
          _id: agentId,
          title: payload.name,
          // `ui_type` is dropped by the backend (undeclared on BaseAgent), so the
          // UI type is carried on `role` — which is required and is what
          // rowFromAgent falls back to for the type badge.
          ui_type: payload.type,
          role: payload.type ?? 'Conversational',
          role_description: payload.description ?? '',
          description: payload.description ?? '',
          // payload.model is a real registry model id (mdl_*) from the create
          // modal (sourced from Connect → AI Models). Store it as-is so the agent
          // runtime resolves it via registry_resolver. NEVER coerce an empty
          // model into a slug default — that produces an unresolvable agent.
          llm: {
            model: payload.model ? toModelId(payload.model) : '',
            temperature: 0,
            max_tokens: 4000,
            risk_level: riskLevelToStored(payload.riskLevel),
            needs_reasoning: needsReasoningToStored(payload.needsReasoning),
          },
          // "What should this agent do?" seeds the system prompt.
          prompt: { template: payload.desc ?? `You are the ${payload.name}.`, variables: [], display_template: null },
          // Skills selected in the create wizard are persisted as ObjectId strings.
          skills: (payload.skillIds ?? []).filter((id) => typeof id === 'string' && id.length > 0),
          memory_config: {
            longTermMemory: true,
            piiRedaction: true,
            topicBoundaries: true,
            jailbreakDefense: true,
          },
          ...(payload.type === 'Voice' ? {
            voice_config: {
              transport: (payload.voiceConfig ?? DEFAULT_VOICE_CONFIG).transport,
              direction: (payload.voiceConfig ?? DEFAULT_VOICE_CONFIG).direction,
              language: (payload.voiceConfig ?? DEFAULT_VOICE_CONFIG).language,
              provider: 'bland',
              connector_id: (payload.voiceConfig ?? DEFAULT_VOICE_CONFIG).connectorId,
              voice: (payload.voiceConfig ?? DEFAULT_VOICE_CONFIG).voice,
              greeting: (payload.voiceConfig ?? DEFAULT_VOICE_CONFIG).greeting,
              allow_interruptions: (payload.voiceConfig ?? DEFAULT_VOICE_CONFIG).allowInterruptions,
              silence_timeout_ms: (payload.voiceConfig ?? DEFAULT_VOICE_CONFIG).silenceTimeoutMs,
              max_call_duration_seconds: (payload.voiceConfig ?? DEFAULT_VOICE_CONFIG).maxCallDurationSeconds,
              record_calls: (payload.voiceConfig ?? DEFAULT_VOICE_CONFIG).recordCalls,
              save_transcripts: (payload.voiceConfig ?? DEFAULT_VOICE_CONFIG).saveTranscripts,
              phone_number_id: (payload.voiceConfig ?? DEFAULT_VOICE_CONFIG).phoneNumberId || undefined,
              transfer_phone_number: (payload.voiceConfig ?? DEFAULT_VOICE_CONFIG).transferPhoneNumber || undefined,
              fabric_tool_access: (payload.voiceConfig ?? DEFAULT_VOICE_CONFIG).fabricToolAccess,
            },
          } : {}),
        },
      );
    } catch (e: any) {
      // The skilled-agent add is what embeds the agent + its model into the flow.
      // If it fails, the flow is an EMPTY SHELL (no agent, no model) that cannot
      // be simulated ("Initial agent not found"). Surface the failure instead of
      // silently returning a hollow agent — the old `catch {}` hid exactly this.
      const detail = e?.response?.data?.detail || e?.message || 'unknown error';
      throw new Error(`Agent created but its configuration could not be saved: ${detail}`);
    }

    return {
      id: makeAgentRef(flowId, agentId),
      flowId,
      agentId,
      flowName: payload.name,
      name: payload.name,
      desc: payload.description ?? '',
      type: payload.type,
      status: 'Draft',
      owner: '—',
      model: toModelLabel(payload.model ? toModelId(payload.model) : undefined),
      lastRun: '—',
      runs: 0,
    };
  },

  // ── Update agent ─────────────────────────────────────────────────────────────
  // Persists builder edits. Agent-level fields (name/prompt/model) go to the
  // embedded skilled agent; the flow name is kept in sync via PUT.
  async updateAgent(
    routeId: string,
    projectId: string,
    data: Partial<AgentConfig & { name: string; status: AgentStatus }>,
  ): Promise<void> {
    const { flowId, agentId } = parseAgentRef(routeId);
    const cfg = (data as any).config ?? {};
    const name = (data as any).name as string | undefined;
    const status = (data as any).status as AgentStatus | undefined;

    if (agentId) {
      const body: any = {};
      if (name) body.title = name;
      if (cfg.modelId) {
        // cfg.modelId is now a real model ID (e.g. 'claude-sonnet-4-6') coming
        // from the builder. toModelId() is kept as a safety net for any legacy
        // label values that might still be in the wild.
        body.llm = { model: toModelId(cfg.modelId), temperature: 0, max_tokens: 4000 };
        // PRD R2: null = not set. Only when the caller passed it, so a save
        // that does not carry the risk level never touches it.
        if (cfg.riskLevel !== undefined) body.llm.risk_level = riskLevelToStored(cfg.riskLevel);
        if (cfg.needsReasoning !== undefined) body.llm.needs_reasoning = needsReasoningToStored(cfg.needsReasoning);
      }
      if (cfg.systemPrompt !== undefined) {
        body.prompt = { template: cfg.systemPrompt || ' ', variables: [], display_template: null };
      }
      if (cfg.description !== undefined) {
        // Persist the human-facing description (distinct from the system prompt).
        // role_description is kept in sync so older read paths stay consistent.
        body.description = cfg.description;
        body.role_description = cfg.description;
      }
      if (cfg.skillIds !== undefined) {
        body.skills = cfg.skillIds;
      }
      if (cfg.memoryConfig !== undefined) {
        body.memory_config = cfg.memoryConfig;
      }
      // FAB-44: persist the KB attach — the backend gates retrieval on
      // has_knowledge_base AND a non-empty context (tags). Send both.
      if (cfg.hasKnowledgeBase !== undefined) {
        body.has_knowledge_base = cfg.hasKnowledgeBase;
      }
      if (cfg.knowledgeContext !== undefined) {
        body.context = cfg.knowledgeContext;
      }
      // Persist the declared output shape. Sent even when emptied ({}) so a user
      // can clear it — the backend drops nulls (exclude_none), so null would be
      // a silent no-op rather than a delete.
      if (cfg.outputSchema !== undefined) {
        body.output_schema = cfg.outputSchema;
      }
      if (cfg.voiceConfig !== undefined) {
        body.voice_config = {
          transport: cfg.voiceConfig.transport,
          direction: cfg.voiceConfig.direction,
          language: cfg.voiceConfig.language,
          provider: 'bland',
          connector_id: cfg.voiceConfig.connectorId,
          bland_agent_id: cfg.voiceConfig.blandAgentId || null,
          // Provider pathways replace Bland's prompt/tools, which would bypass
          // the deployed Fabric flow. Clear any legacy value when this agent is
          // saved; Fabric owns orchestration and Bland only carries the audio.
          pathway_id: null,
          voice: cfg.voiceConfig.voice,
          greeting: cfg.voiceConfig.greeting,
          allow_interruptions: cfg.voiceConfig.allowInterruptions,
          silence_timeout_ms: cfg.voiceConfig.silenceTimeoutMs,
          max_call_duration_seconds: cfg.voiceConfig.maxCallDurationSeconds,
          record_calls: cfg.voiceConfig.recordCalls,
          save_transcripts: cfg.voiceConfig.saveTranscripts,
          phone_number_id: cfg.voiceConfig.phoneNumberId || null,
          transfer_phone_number: cfg.voiceConfig.transferPhoneNumber || null,
          fabric_tool_access: cfg.voiceConfig.fabricToolAccess,
        };
      }
      // FAB-60: persist the Tables access selection.
      if (cfg.tablesAccess !== undefined) {
        body.tables_access = {
          enabled: cfg.tablesAccess.enabled,
          table_ids: cfg.tablesAccess.tableIds,
          mode: cfg.tablesAccess.mode,
        };
      }
      // DESIGN-agent-context-access: persist the Context-from-Graph switch.
      //
      // Sent only when the caller supplied it. The backend helper merges with
      // exclude_unset, so omitting the key preserves whatever is stored — which
      // is what the preview/run path (which persists a PARTIAL config before
      // kicking off a run) relies on to avoid silently re-enabling the graph on
      // an agent the user switched off.
      //
      // The whole sub-document is replaced, matching the flow builder and what
      // api-gateway's update-process-flow documents. A `brief_only` set through
      // the manifest tools is therefore normalised to `full` by a console save:
      // both builders are two-ended switches over a three-valued field.
      if (cfg.contextAccess !== undefined) {
        body.context_access = {
          mode: cfg.contextAccess.mode,
          focus_labels: cfg.contextAccess.focusLabels ?? [],
        };
      }
      // FAB-182: persist trigger hand-offs. Drop a trigger with no target flow
      // picked yet (mirrors the flow builder's own filter in AgentFlowOptions) —
      // the backend rejects an empty agentflow_id anyway, but silently, one
      // trigger's worth of failure shouldn't block saving the rest of the form.
      if (cfg.triggers !== undefined) {
        body.triggers = (cfg.triggers as AgentTrigger[])
          .filter((t: AgentTrigger) => t.name && t.agentflow_id)
          .map((t: AgentTrigger) => ({
            ...(t._id ? { _id: t._id } : {}),
            name: t.name,
            goal: t.goal,
            agentflow_id: t.agentflow_id,
            start_from_agent_id: t.start_from_agent_id || undefined,
          }));
      }
      await dataServiceClient.post<any>(
        `/agentflow/${encodeURIComponent(flowId)}/update/skilled/${encodeURIComponent(agentId)}`,
        body,
      );
    }

    // Keep the flow's display name + description aligned with the (primary)
    // agent. This also covers flow-as-agent rows (no embedded agentId) so their
    // description still persists, and keeps flow.description fresh for the list's
    // flow-as-agent read path. The data-service validator requires description
    // length >= 3, so only send it when it qualifies.
    const flowDesc = typeof cfg.description === 'string' ? cfg.description.trim() : undefined;
    const sendFlowDesc = !!flowDesc && flowDesc.length >= 3;
    if (name || sendFlowDesc || status !== undefined) {
      const put = dataServiceClient.put<any>(`/agentflow/${encodeURIComponent(flowId)}`, {
        ...(name ? { name } : {}),
        ...(sendFlowDesc ? { description: flowDesc } : {}),
        // Deploy is the only action that persists status — it flips the
        // agentflow's isActive flag so normalizeStatus reads it as Active
        // instead of Draft, and the builder's chat panel unlocks.
        ...(status !== undefined ? { isActive: status === 'Active' } : {}),
        projectID: projectId,
      });
      if (status !== undefined) {
        await put; // Deploy must actually persist — let failures surface, don't hide them.
      } else {
        await put.catch(() => {}); // name/description sync alone stays best-effort.
      }
    }
  },

  // ── Delete agent ─────────────────────────────────────────────────────────────
  // On the Agents registry every row is a standalone (kind=agent) container — its
  // own AgentFlow holding a single agent. Deleting must remove the WHOLE container,
  // not just the embedded agent; otherwise an empty flow lingers and re-renders as
  // a flow-as-agent placeholder row (the agent appears "undeletable").
  async deleteAgent(routeId: string, projectId: string): Promise<void> {
    const { flowId } = parseAgentRef(routeId);
    await dataServiceClient.delete<any>(
      `/agentflow/${encodeURIComponent(projectId)}/${encodeURIComponent(flowId)}`,
    );
  },

  // ── Clone agent ──────────────────────────────────────────────────────────────
  async cloneAgent(routeId: string, projectId: string, name: string): Promise<AgentRow> {
    const { flowId } = parseAgentRef(routeId);
    // `coworker_name` is required by CopyAgentflowInput — omitting it 422'd on
    // every call. The `projectID` query param is what check_authorization
    // resolves the caller's project membership against (FAB-185); without it
    // the destination is never actually validated.
    const res = await dataServiceClient.post<any>(
      `/agentflow/copy?projectID=${encodeURIComponent(projectId)}`,
      {
        agentflow_id: flowId,
        projectID: projectId,
        copy_skills: true,
        coworker_name: name,
      },
    );
    const flow = res.agentflow ?? res;
    const agents: any[] = flow.agents ?? [];
    return agents.length ? rowFromAgent(flow, agents[0]) : rowFromFlow(flow);
  },

  // ── Interop: discover capabilities ───────────────────────────────────────────
  // FAB-48: connects to the external agent's real MCP endpoint (harness
  // negotiates the handshake — see SPEC-interop-agent.md protocol contract)
  // and returns its actual tool list, not a canned sample.
  async discoverCapabilities(
    payload: InteropDiscoverPayload,
  ): Promise<{ capabilities: Capability[]; serverName?: string; card?: InteropAgentCard }> {
    const res = await apiClient.post<{ ok: boolean; capabilities?: Capability[]; serverName?: string; card?: InteropAgentCard }>(
      '/api/v1/agents/interop/discover',
      payload,
    );
    return { capabilities: res.capabilities ?? [], serverName: res.serverName, card: res.card };
  },

  // ── Interop: register agent ───────────────────────────────────────────────────
  // FAB-48: persists the registration, runs a best-effort Discover so the
  // Skill row starts with real capabilities, and creates the Skill row.
  async registerInteropAgent(payload: RegisterInteropPayload): Promise<{ agentId: string; skillId: string }> {
    const res = await apiClient.post<{ agentId: string; skillId: string; type: string; status: string }>(
      '/api/v1/agents/interop',
      payload,
    );
    return { agentId: res.agentId, skillId: res.skillId };
  },

  // ── Interop OUTBOUND: registrations (connections Fabric made to external agents) ──

  async listInteropRegistrations(projectId: string): Promise<InteropRegistration[]> {
    const res = await apiClient.get<{ registrations: InteropRegistration[] }>(
      `/api/v1/agents/interop?projectId=${encodeURIComponent(projectId)}`,
    );
    return res.registrations ?? [];
  },

  async getInteropRegistration(id: string, projectId: string): Promise<InteropRegistration | null> {
    const all = await agentsService.listInteropRegistrations(projectId);
    return all.find((r) => r.id === id) ?? null;
  },

  // Runs one discovered capability against the external agent, right now —
  // the "Test call" panel. Result is whatever the external MCP tool returned.
  async callInteropTool(id: string, tool: string, args: Record<string, any>): Promise<any> {
    return apiClient.post<any>(`/api/v1/agents/interop/${encodeURIComponent(id)}/call`, {
      tool,
      arguments: args,
    }, { timeout: 60000 });
  },

  // ── Interop OUTBOUND: single_peer_agent invoke/resume — SPEC-interop-agent-v2.md §4 ──
  // Same call/response shape doInvoke returns on the harness side: an
  // Outcome{kind, handle, result, ask, failErr}. Used by the connection
  // detail page's "Test call" panel for a single_peer_agent registration
  // (goal in, one call — the flow-runtime path itself goes through
  // data-service, not this client, since that's a server-to-server call).
  async invokeInteropAgent(id: string, goal: string, context?: Record<string, any>): Promise<InteropOutcome> {
    return apiClient.post<InteropOutcome>(`/api/v1/agents/interop/${encodeURIComponent(id)}/call`, {
      goal,
      context,
    }, { timeout: 60000 });
  },

  async resumeInteropAgent(id: string, handle: string, response?: Record<string, any>): Promise<InteropOutcome> {
    return apiClient.post<InteropOutcome>(`/api/v1/agents/interop/${encodeURIComponent(id)}/call`, {
      handle,
      response,
    }, { timeout: 60000 });
  },

  // ── Interop OUTBOUND: MCP elicitation/create — SPEC-interop-agent-v2.md §8 test item #8 ──
  // Bounded, synchronous — the harness call above (invokeInteropAgent) sits
  // blocked the whole time, so this is polled WHILE that call is still in
  // flight, not after it returns (contrast resumeInteropAgent, which answers
  // an A2A task that already came back as kind:'pending'). registrationId,
  // not an execution id — the harness's ElicitationStore is keyed that way.
  // projectId is required: claims.SuperAgentProjectID() (the fallback these
  // routes used before) reads a JWT product claim nothing in the current
  // codebase ever populates, so passing it explicitly is the only way this
  // resolves to a real project — see projectIDFromRequest in harness's
  // handler.go.
  async getElicitation(registrationId: string, projectId: string): Promise<InteropElicitationAsk | null> {
    const res = await apiClient.get<{ pending: boolean; ask?: InteropElicitationAsk }>(
      `/api/v1/agents/interop/${encodeURIComponent(registrationId)}/elicitation?projectId=${encodeURIComponent(projectId)}`,
    );
    return res.pending ? (res.ask ?? null) : null;
  },

  async answerElicitation(
    registrationId: string,
    projectId: string,
    action: 'accept' | 'decline' | 'cancel',
    content?: Record<string, any>,
  ): Promise<void> {
    await apiClient.post<{ ok: boolean }>(
      `/api/v1/agents/interop/${encodeURIComponent(registrationId)}/elicitation?projectId=${encodeURIComponent(projectId)}`,
      { action, content },
    );
  },

  // Which Fabric agents still have this connection's Skill attached — the
  // pre-delete check. Non-empty means Delete must be blocked until the user
  // detaches it from each one (deleting out from under an attached agent
  // would silently break that agent's next run).
  async getInteropAttachedAgents(id: string): Promise<InteropAttachedAgent[]> {
    const res = await apiClient.get<{ agents: InteropAttachedAgent[] }>(
      `/api/v1/agents/interop/${encodeURIComponent(id)}/attached-agents`,
    );
    return res.agents ?? [];
  },

  // Deletes the registration. Rejects with a 409 (surfaced via apiClient's
  // normal error path) carrying { error, agents } when it's still attached —
  // callers should call getInteropAttachedAgents first to show that list
  // proactively rather than relying solely on this error.
  async deleteInteropRegistration(id: string): Promise<void> {
    await apiClient.delete<void>(`/api/v1/agents/interop/${encodeURIComponent(id)}`);
  },

  // ── Interop INBOUND: publish one of my agents, manage the keys that let ──────
  // ── external MCP callers reach it ────────────────────────────────────────────

  // Every agent-kind flow in the project, with its current publish state —
  // powers the "pick an agent to publish" step. Reuses the same list call as
  // listAgents (the /agentflows/minimal projection doesn't carry interop_enabled).
  // The backend's kind=agent filter is shared with the main Agents list and
  // deliberately doesn't discriminate by ui_type, so Proxy/Interop rows come
  // back too — filtered out here, not server-side, since this endpoint isn't
  // interop-specific. Proxy already has its own dedicated OpenAI-compatible
  // endpoint (a second, MCP-shaped exposure of the same agent is redundant
  // with what Proxy IS); Interop (outbound) rows are peer-connection pointers
  // with no goal/execution of their own to run inbound.
  async listPublishableAgents(projectId: string): Promise<{ id: string; name: string; type: AgentType; interopEnabled: boolean }[]> {
    const res = await dataServiceClient.get<{ agentflows: any[] }>(
      `/agentflows?projectID=${encodeURIComponent(projectId)}&kind=agent&skip=1&limit=100`,
    );
    const flows: any[] = res.agentflows ?? [];
    return flows
      .map((flow) => ({
        id: flow._id,
        name: flow.name ?? 'Untitled agent',
        type: normalizeType(flow.ui_type ?? flow.agents?.[0]?.role ?? flow.type),
        interopEnabled: !!flow.interop_enabled,
      }))
      .filter((a) => a.type !== 'Proxy' && a.type !== 'Interop');
  },

  async setAgentInteropEnabled(flowId: string, projectId: string, enabled: boolean): Promise<void> {
    await dataServiceClient.put(`/agentflow/${encodeURIComponent(flowId)}?projectID=${encodeURIComponent(projectId)}`, {
      interop_enabled: enabled,
    });
  },

  // flowId scopes the minted key to exactly this one agent (SPEC §3) — a key
  // minted from one agent's Publish page must never reach any other agent.
  async mintInteropKey(projectId: string, flowId: string, name: string): Promise<{ key: string; id: string; name: string; prefix: string }> {
    return apiClient.post<{ key: string; id: string; name: string; prefix: string }>(
      '/api/v1/mcp/interop/keys',
      { projectId, flowId, name },
    );
  },

  // Same scoping as mint — each agent's Publish page only ever sees the
  // keys minted for that agent, not every key in the project.
  async listInteropKeys(projectId: string, flowId: string): Promise<InboundInteropKey[]> {
    const res = await apiClient.get<{ keys: InboundInteropKey[] }>(
      `/api/v1/mcp/interop/keys?projectId=${encodeURIComponent(projectId)}&flowId=${encodeURIComponent(flowId)}`,
    );
    return res.keys ?? [];
  },

  async revokeInteropKey(id: string): Promise<void> {
    await apiClient.delete(`/api/v1/mcp/interop/keys/${encodeURIComponent(id)}`);
  },

  // The address an external MCP client (ServiceNow, Claude, a custom client…)
  // connects to for the inbound path. In this dev environment that's the
  // harness's direct port; behind a real deployment it should be the public
  // API domain instead.
  getInboundMcpUrl(): string {
    if (typeof window === 'undefined') return '/api/v1/mcp/interop';
    return `${window.location.protocol}//${window.location.hostname}:7003/api/v1/mcp/interop`;
  },

  // Full skill doc, including interop_capabilities when it's an interop
  // skill — powers the "what can this actually do" card in the skill picker
  // (agent-create-modal.tsx), reusing the existing GET /skills/{id} route
  // rather than adding a new one.
  async getSkillDetails(skillId: string): Promise<{ interopCapabilities?: Capability[] } & Record<string, any>> {
    const res = await dataServiceClient.get<any>(`/skills/${encodeURIComponent(skillId)}`);
    return { ...res, interopCapabilities: res.interop_capabilities };
  },

  // ── Test agent (live preview · capability reply) ─────────────────────────────
  // The builder preview only calls this for *basic chat* (greetings / "what can
  // you do?") — an explicit action request runs the real agent instead. So this
  // produces a conversational, capability-aware reply derived from the agent's
  // attached skills WITHOUT executing anything. (FABRIC-STUB: there is no harness
  // agent-dialogue endpoint yet — `/api/v1/agents/{id}/test` doesn't exist — so
  // this is synthesized client-side; swap for the network call when it lands.)
  async testAgent(
    message: string,
    ctx?: { agentName?: string; modelLabel?: string; systemPrompt?: string; skillNames?: string[] },
  ): Promise<{ reply: string }> {
    const name = ctx?.agentName?.trim() || 'this agent';
    const model = ctx?.modelLabel?.trim();
    const msg = message.trim();

    // Humanize "Connector - Action · Connector" → { connector, action }.
    const caps = (ctx?.skillNames ?? []).filter(Boolean).map((s) => {
      const noConn = s.replace(/\s*·\s*[^·]+$/, '').trim(); // drop the "· Linear" suffix
      const m = noConn.match(/^(.*?)\s*-\s*(.*)$/);          // "Linear - Get issues"
      const connector = (m ? m[1] : '').trim();
      const action = (m ? m[2] : noConn).trim();
      return { connector, action };
    });

    const greeting = /^(hi+|hey+|hello+|yo|sup|gm|gn|good (morning|afternoon|evening|night)|thanks?|thank you|namaste|hola|howdy)\b/i.test(msg);

    // Build a natural example prompt from the verbs the agent actually has.
    const verbs = caps.map((c) => c.action.toLowerCase());
    const fetchVerb = caps.find((c) => /get|fetch|list|search|read|pull|find/i.test(c.action));
    const actVerb = caps.find((c) => /send|email|create|update|post|reply|notify|draft/i.test(c.action));
    const example =
      fetchVerb && actVerb
        ? `${fetchVerb.action.toLowerCase()} and ${actVerb.action.toLowerCase().replace(/^send (an? )?/, 'email ')}`
        : caps[0]
        ? caps[0].action.toLowerCase()
        : '';

    const lines: string[] = [];
    lines.push(`${greeting ? 'Hi! 👋 ' : ''}I'm **${name}**${model ? `, running on ${model}` : ''}.`);

    if (caps.length) {
      lines.push('', 'Here’s what I can do for you:');
      for (const c of caps) {
        lines.push(c.connector ? `• **${c.connector}** — ${c.action.toLowerCase()}` : `• ${c.action}`);
      }
      if (example) {
        lines.push('', `Just tell me what you need — e.g. *“${example}”* — and I’ll run it for real.`);
      } else {
        lines.push('', `Tell me what you’d like done and I’ll run it for real.`);
      }
    } else {
      lines.push('', `I don’t have any skills attached yet, so I can chat but can’t take real actions until you add one under **Tools & Skills**.`);
    }

    return { reply: lines.join('\n') };
  },
};
